import { useState, useEffect, useCallback } from 'react'
import { productVariantService, variantTypeService } from '../../../services/erpApi'
import { Layers, Plus, X, Save, Loader2, GripVertical } from 'lucide-react'
import { useToast } from '../../../hooks/useToast'
import Toast from '../../Toast/Toast'
import './ProductVariantsManager.css'

/**
 * Componente para gerenciar variações de produtos (cor, tamanho, etc.)
 */
function ProductVariantsManager({ productId }) {
  const [variants, setVariants] = useState({})
  const [variantTypes, setVariantTypes] = useState([]) // Tipos salvos do banco
  const [loading, setLoading] = useState(false)
  const [loadingTypes, setLoadingTypes] = useState(false)
  const [saving, setSaving] = useState(false)
  const { toast, showToast, hideToast } = useToast()
  
  // Estado para adicionar nova variação
  const [newVariantType, setNewVariantType] = useState('')
  const [newVariantCustomType, setNewVariantCustomType] = useState('')
  const [newVariantValue, setNewVariantValue] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)

  const loadVariantTypes = useCallback(async () => {
    try {
      setLoadingTypes(true)
      const response = await variantTypeService.getAll()
      if (response.success) {
        setVariantTypes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar tipos de variação:', error)
    } finally {
      setLoadingTypes(false)
    }
  }, [])

  const loadVariants = useCallback(async () => {
    try {
      setLoading(true)
      const response = await productVariantService.getByProductId(productId)
      if (response.success) {
        // Garantir que sempre seja um objeto, mesmo se response.data for null/undefined
        setVariants(response.data || {})
      } else {
        // Se não houver sucesso, inicializar como objeto vazio
        setVariants({})
      }
    } catch (error) {
      console.error('Erro ao carregar variações:', error)
      // Em caso de erro, manter estado vazio
      setVariants({})
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    if (productId) {
      loadVariants()
      loadVariantTypes()
    }
  }, [productId, loadVariants, loadVariantTypes])

  const handleAddVariant = async () => {
    const finalType = newVariantType === 'custom' ? newVariantCustomType : newVariantType
    
    if (!finalType || !newVariantValue) {
      showToast('Tipo e valor são obrigatórios', 'warning')
      return
    }

    const variantTypeName = finalType.toLowerCase().trim()
    const variantValue = newVariantValue.trim()

    // Verificar se já existe variação com mesmo tipo e valor
    if (variants[variantTypeName]?.some(v => v.value.toLowerCase() === variantValue.toLowerCase())) {
      showToast('Esta variação já existe', 'warning')
      return
    }

    // Se for um tipo personalizado (custom), criar/salvar o tipo primeiro
    if (newVariantType === 'custom' && newVariantCustomType) {
      try {
        const typeDisplayName = newVariantCustomType.trim()
        const response = await variantTypeService.create({
          name: variantTypeName,
          displayName: typeDisplayName.charAt(0).toUpperCase() + typeDisplayName.slice(1)
        })
        if (response.success) {
          // Atualizar lista de tipos
          await loadVariantTypes()
        }
      } catch (error) {
        console.error('Erro ao criar tipo de variação:', error)
        // Continuar mesmo se houver erro (tipo pode já existir)
      }
    }

    // Adicionar à lista
    setVariants(prev => ({
      ...prev,
      [variantTypeName]: [
        ...(prev[variantTypeName] || []),
        {
          value: variantValue,
          displayOrder: prev[variantTypeName]?.length || 0,
          id: Date.now() // ID temporário
        }
      ]
    }))

    setNewVariantType('')
    setNewVariantCustomType('')
    setNewVariantValue('')
    setShowAddForm(false)
  }

  const handleRemoveVariant = (type, value) => {
    setVariants(prev => {
      const newVariants = { ...prev }
      if (newVariants[type]) {
        newVariants[type] = newVariants[type].filter(v => v.value !== value)
        if (newVariants[type].length === 0) {
          delete newVariants[type]
        }
      }
      return newVariants
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Converter para formato esperado pela API
      // O backend irá criar os tipos automaticamente se não existirem
      const variantsArray = []
      Object.keys(variants).forEach(type => {
        variants[type].forEach((variant, index) => {
          // Validar se variant.value existe e não está vazio
          if (!variant.value || !variant.value.trim()) {
            throw new Error(`Valor da variação não pode estar vazio para o tipo "${type}"`)
          }
          
          // Buscar displayName do tipo se existir na lista
          const typeInfo = variantTypes.find(t => t.name === type)
          variantsArray.push({
            type: type.trim(),
            displayName: typeInfo?.display_name || type,
            value: variant.value.trim(),
            displayOrder: index
          })
        })
      })

      const response = await productVariantService.updateProductVariants(productId, variantsArray)
      if (response.success) {
        await loadVariants()
        await loadVariantTypes() // Recarregar tipos caso algum novo tenha sido criado
        showToast('✓ Variações atualizadas com sucesso!', 'success')
      } else {
        throw new Error(response.message || 'Erro ao salvar variações')
      }
    } catch (error) {
      console.error('Erro ao salvar variações:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Erro desconhecido ao salvar variações'
      showToast(errorMessage, 'error')
    } finally {
      setSaving(false)
    }
  }

  const variantTypeKeys = Object.keys(variants) // Chaves dos tipos que já têm variações

  if (loading || loadingTypes) {
    return (
      <div className="product-variants-manager">
        <div className="loading">
          <Loader2 className="spinner" size={24} />
          <span>Carregando variações...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
      <div className="product-variants-manager">
      <div className="variants-header">
        <Layers size={20} />
        <h3>Variações do Produto</h3>
        <button
          className="btn-save-variants"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="spinner" size={16} />
              Salvando...
            </>
          ) : (
            <>
              <Save size={16} />
              Salvar Variações
            </>
          )}
        </button>
      </div>

      {variantTypeKeys.length === 0 && !showAddForm ? (
        <div className="no-variants">
          <p>Este produto não possui variações cadastradas.</p>
          <button
            className="btn-add-first-variant"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Adicionar Primeira Variação
          </button>
        </div>
      ) : (
        <>
          {/* Lista de variações por tipo */}
          {variantTypeKeys.map(type => {
            // Buscar display name do tipo se existir
            const typeInfo = variantTypes.find(t => t.name === type)
            const displayName = typeInfo?.display_name || (type.charAt(0).toUpperCase() + type.slice(1))
            return (
            <div key={type} className="variant-type-group">
              <div className="variant-type-header">
                <h4>{displayName}</h4>
                <button
                  className="btn-add-variant-value"
                  onClick={() => {
                    setNewVariantType(type)
                    setNewVariantValue('')
                    setShowAddForm(true)
                  }}
                >
                  <Plus size={14} />
                  Adicionar {displayName}
                </button>
              </div>
              <div className="variant-values">
                {variants[type].map((variant, index) => (
                  <div key={variant.id || index} className="variant-value-item">
                    <GripVertical size={16} className="drag-handle" />
                    <span className="variant-value">{variant.value}</span>
                    <button
                      className="btn-remove-variant"
                      onClick={() => handleRemoveVariant(type, variant.value)}
                      title="Remover variação"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            )
          })}

          {/* Formulário para adicionar nova variação */}
          {showAddForm && (
            <div className="add-variant-form">
              <h4>Adicionar Variação</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Variação</label>
                  <select
                    value={newVariantType}
                                onChange={(e) => {
                      setNewVariantType(e.target.value)
                      setNewVariantCustomType('') // Limpar campo custom quando mudar seleção
                    }}
                    disabled={loadingTypes}
                  >
                    <option value="">Selecione um tipo</option>
                    {variantTypes.map(type => (
                      <option key={type.id} value={type.name}>
                        {type.display_name}
                      </option>
                    ))}
                    <option value="custom">+ Criar novo tipo</option>
                  </select>
                  {newVariantType === 'custom' && (
                    <input
                      type="text"
                      placeholder="Digite o nome do tipo (ex: Largura do Assento)"
                      value={newVariantCustomType}
                      onChange={(e) => setNewVariantCustomType(e.target.value)}
                      className="custom-type-input"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Valor</label>
                  <input
                    type="text"
                    value={newVariantValue}
                    onChange={(e) => setNewVariantValue(e.target.value)}
                    placeholder="Ex: Preto, P, M, G"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddVariant()
                      }
                    }}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowAddForm(false)
                    setNewVariantType('')
                    setNewVariantCustomType('')
                    setNewVariantValue('')
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn-add"
                  onClick={handleAddVariant}
                >
                  <Plus size={16} />
                  Adicionar
                </button>
              </div>
            </div>
          )}

          {!showAddForm && (
            <button
              className="btn-add-new-type"
              onClick={() => {
                setNewVariantType('')
                setNewVariantCustomType('')
                setNewVariantValue('')
                setShowAddForm(true)
              }}
            >
              <Plus size={16} />
              Adicionar Novo Tipo de Variação
            </button>
          )}
        </>
      )}

      {variantTypeKeys.length > 0 && (
        <div className="variants-info">
          <small>
            💡 As variações serão exibidas no e-commerce para que clientes possam selecionar
            a opção desejada (ex: Cor: Preto, Tamanho: M). Tipos personalizados criados ficarão disponíveis para reutilização em outros produtos.
          </small>
        </div>
      )}
    </div>
    </>
  )
}

export default ProductVariantsManager

